import {
  Box,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { FiFilter, FiSearch } from "react-icons/fi";

import { Button } from "../../../components";

type StatusOption = {
  value: string;
  label: string;
};

type StatusCounts = {
  invited: number;
  inProgress: number;
  summaryFailure: number;
  completed: number;
};

const STATUS_OPTIONS: StatusOption[] = [
  { value: "invited", label: "Invited" },
  { value: "inProgress", label: "In Progress" },
  { value: "summaryFailure", label: "Error" },
  { value: "completed", label: "Completed" },
];

type StatusFiltersProps = {
  activeStatuses: string[];
  statusCounts?: StatusCounts;
  onToggle: (statusValue: string) => void;
};

const StatusFilters: React.FC<StatusFiltersProps> = ({
  activeStatuses,
  statusCounts,
  onToggle,
}) => {
  const getStatusCount = (statusValue: string) => {
    if (!statusCounts) return 0;

    switch (statusValue) {
      case "invited":
        return statusCounts.invited;
      case "inProgress":
        return statusCounts.inProgress;
      case "summaryFailure":
        return statusCounts.summaryFailure;
      case "completed":
        return statusCounts.completed;
      default:
        return 0;
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={3}>
        Status
      </FormLabel>
      <Stack spacing={3}>
        {STATUS_OPTIONS.map((option) => {
          const count = getStatusCount(option.value);
          return (
            <Checkbox
              key={option.value}
              data-testid={`status-filter-${option.value}`}
              isChecked={activeStatuses.includes(option.value)}
              onChange={() => onToggle(option.value)}
              size="md"
              colorScheme="blue"
              variant="gray.500"
            >
              <Flex alignItems="center" gap={2}>
                <Text fontSize="sm" color="gray.900">
                  {option.label}
                </Text>
                <Text fontSize="sm" color="gray.900">
                  ({count})
                </Text>
              </Flex>
            </Checkbox>
          );
        })}
      </Stack>
    </FormControl>
  );
};

type AiInterviewerCandidatesFiltersProps = {
  keyword: string;
  statuses: string[];
  onKeywordChange: (keyword: string) => void;
  onStatusesChange: (statuses: string[]) => void;
  /** Called on mobile when Apply Filters is clicked - batches keyword and status updates */
  onApplyFilters?: (keyword: string, statuses: string[]) => void;
  totalCount?: number;
  isMobile?: boolean;
  statusCounts?: StatusCounts;
  isSearching?: boolean;
};

type AiInterviewerCandidatesFiltersBodyProps = {
  keyword: string;
  statuses: string[];
  onKeywordChange: (keyword: string) => void;
  onStatusesChange: (statuses: string[]) => void;
  statusCounts?: StatusCounts;
  isSearching?: boolean;
};

const AiInterviewerCandidatesFiltersBodyComponent: React.FC<
  AiInterviewerCandidatesFiltersBodyProps
> = ({
  keyword,
  statuses,
  onKeywordChange,
  onStatusesChange,
  statusCounts,
  isSearching,
}) => {
  const handleStatusToggle = (statusValue: string) => {
    const nextStatuses = statuses.includes(statusValue)
      ? statuses.filter((s) => s !== statusValue)
      : [...statuses, statusValue];
    onStatusesChange(nextStatuses);
  };

  const handleKeywordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onKeywordChange(e.target.value);
  };

  return (
    <>
      <InputGroup mb={6}>
        <InputLeftElement h="40px" pointerEvents="none" color="gray.400">
          <FiSearch />
        </InputLeftElement>
        <Input
          placeholder="Keyword"
          value={keyword}
          onChange={handleKeywordInput}
          h="40px"
          bg="white"
          borderColor="gray.200"
          _placeholder={{ color: "gray.400" }}
          pr={isSearching ? "40px" : undefined}
        />
        {isSearching && (
          <InputRightElement h="40px" pointerEvents="none">
            <Spinner size="sm" color="gray.400" />
          </InputRightElement>
        )}
      </InputGroup>
      <StatusFilters
        activeStatuses={statuses}
        statusCounts={statusCounts}
        onToggle={handleStatusToggle}
      />
    </>
  );
};

export const AiInterviewerCandidatesFiltersBody = React.memo(
  AiInterviewerCandidatesFiltersBodyComponent
);

const AiInterviewerCandidatesFilters: React.FC<
  AiInterviewerCandidatesFiltersProps
> = ({
  keyword,
  statuses,
  onKeywordChange,
  onStatusesChange,
  onApplyFilters,
  totalCount,
  isMobile,
  statusCounts,
  isSearching,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [mobileStatuses, setMobileStatuses] = useState(statuses);
  const [mobileKeyword, setMobileKeyword] = useState(keyword);

  useEffect(() => {
    if (!isOpen) {
      setMobileStatuses(statuses);
      setMobileKeyword(keyword);
    }
  }, [isOpen, statuses, keyword]);

  const activeStatuses = mobileStatuses;
  // For mobile drawer, check if there are pending changes (keyword or statuses differ from applied)
  const hasPendingMobileFilters = useMemo(
    () => mobileKeyword.trim().length > 0 || mobileStatuses.length > 0,
    [mobileKeyword, mobileStatuses]
  );
  const totalUnfilteredCount = useMemo(() => {
    if (!statusCounts) return totalCount ?? 0;
    return (
      statusCounts.invited +
      statusCounts.inProgress +
      statusCounts.summaryFailure +
      statusCounts.completed
    );
  }, [statusCounts, totalCount]);

  const handleResetFilters = () => {
    onKeywordChange("");
    onStatusesChange([]);
    setMobileStatuses([]);
    setMobileKeyword("");
    onClose();
  };
  const handleClearMobileKeyword = () => {
    setMobileKeyword("");
  };
  const getStatusLabel = (statusValue: string) =>
    STATUS_OPTIONS.find((option) => option.value === statusValue)?.label ??
    statusValue;

  const handleMobileKeywordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMobileKeyword(e.target.value);
  };

  return (
    <>
      {/* Mobile filters - collapsed button that opens drawer */}
      <Button
        leftIcon={<FiFilter size={18} />}
        onClick={() => {
          setMobileStatuses(statuses);
          onOpen();
        }}
        variant="ghost"
        colorScheme="blue"
        size="sm"
        fontWeight="medium"
      >
        Filters
      </Button>

      {/* Mobile drawer - status filters and keyword search */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
          <DrawerBody pt={6} pb={24}>
            <Stack spacing={6}>
              {/* Keyword Search */}
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={3}
                >
                  Keyword
                </FormLabel>
                <InputGroup>
                  <InputLeftElement
                    h="40px"
                    pointerEvents="none"
                    color="gray.400"
                  >
                    <FiSearch />
                  </InputLeftElement>
                  <Input
                    placeholder="Filter by keyword"
                    value={mobileKeyword}
                    onChange={handleMobileKeywordInput}
                    h="40px"
                    bg="white"
                    borderColor="gray.200"
                    _placeholder={{ color: "gray.400" }}
                  />
                </InputGroup>
              </FormControl>

              {/* Status Filters */}
              <StatusFilters
                activeStatuses={activeStatuses}
                statusCounts={statusCounts}
                onToggle={(statusValue) => {
                  const nextStatuses = activeStatuses.includes(statusValue)
                    ? activeStatuses.filter((s) => s !== statusValue)
                    : [...activeStatuses, statusValue];
                  setMobileStatuses(nextStatuses);
                }}
              />

              {/* Pending Filters Preview */}
              {hasPendingMobileFilters && (
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.600"
                    mb={3}
                  >
                    Pending Filters
                  </Text>
                  <Wrap spacing={2}>
                    {mobileKeyword.trim().length > 0 ? (
                      <WrapItem>
                        <Tag size="sm" variant="subtle" colorScheme="blue">
                          <TagLabel>Keyword: {mobileKeyword.trim()}</TagLabel>
                          <TagCloseButton onClick={handleClearMobileKeyword} />
                        </Tag>
                      </WrapItem>
                    ) : null}
                    {activeStatuses.map((status) => (
                      <WrapItem key={status}>
                        <Tag size="sm" variant="subtle" colorScheme="blue">
                          <TagLabel>{getStatusLabel(status)}</TagLabel>
                          <TagCloseButton
                            onClick={() => {
                              const nextStatuses = activeStatuses.filter(
                                (s) => s !== status
                              );
                              setMobileStatuses(nextStatuses);
                            }}
                          />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}

              {/* Count */}
              <Text fontSize="sm" color="gray.600">
                Showing {totalCount ?? 0} of {totalUnfilteredCount}
              </Text>
            </Stack>
          </DrawerBody>
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            p={4}
            bg="white"
            borderTopWidth="1px"
            borderTopColor="gray.200"
          >
            <HStack spacing={3}>
              <Button
                flex="1"
                variant="outline"
                onClick={handleResetFilters}
                isDisabled={!hasPendingMobileFilters}
              >
                Reset
              </Button>
              <Button
                flex="1"
                colorScheme="blue"
                onClick={() => {
                  if (onApplyFilters) {
                    onApplyFilters(mobileKeyword, activeStatuses);
                  } else {
                    onKeywordChange(mobileKeyword);
                    onStatusesChange(activeStatuses);
                  }
                  onClose();
                }}
              >
                Apply Filters
              </Button>
            </HStack>
          </Box>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default AiInterviewerCandidatesFilters;
